import { useRef, useState } from "react";
import * as htmlToImage from "html-to-image";
import { motion, AnimatePresence } from "framer-motion";
import { Share, Download, X, Sparkle, Target } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * Celebration banner + branded shareable PNG for a new handicap milestone.
 * Shows a small inline banner on the Stats page; "Share" opens a modal with
 * a 9:16 PNG of the milestone. Dismissing or sharing marks the milestone seen
 * server-side (so it never re-fires).
 */
export default function HandicapMilestoneCard({ milestone, userName, onDismiss }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [dismissed, setDismissed] = useState(false);
  const cardRef = useRef(null);

  if (!milestone || dismissed) return null;

  const markSeen = async () => {
    try {
      await api.post("/stats/milestones/seen", { code: milestone.code });
    } catch {
      /* ignore — best effort */
    }
  };

  const dismiss = async () => {
    setDismissed(true);
    await markSeen();
    onDismiss?.();
  };

  const buildBlob = async () => {
    if (!cardRef.current) return null;
    return await htmlToImage.toBlob(cardRef.current, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: "#0c2a20",
    });
  };

  const share = async () => {
    setBusy(true);
    setToast("");
    try {
      const blob = await buildBlob();
      if (!blob) throw new Error("Could not render card");
      const filename = `fairway-handicap-${milestone.code}.png`;
      const file = new File([blob], filename, { type: "image/png" });
      const shareText = `Just hit ${milestone.label.toLowerCase()} on Fairway ${milestone.emoji} — handicap ${milestone.handicap_index.toFixed(1)}. Catch me if you can.`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "New handicap milestone",
          text: shareText,
        });
        setToast("Shared!");
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        setToast("Saved to downloads");
      }
      await markSeen();
    } catch (err) {
      if (err?.name !== "AbortError") setToast("Could not share — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -8, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", damping: 18, stiffness: 220 }}
        className="relative overflow-hidden rounded-3xl p-4 mb-4 text-[#F2EEE7]"
        style={{
          background: `linear-gradient(135deg, #143D2F 0%, ${milestone.accent} 200%)`,
        }}
        data-testid="handicap-milestone-banner"
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss milestone"
          data-testid="handicap-milestone-dismiss"
          className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.25)", color: "#F2EEE7" }}
        >
          <X size={14} weight="bold" />
        </button>
        <div className="flex items-start gap-3 pr-7">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
            style={{ background: "rgba(242,238,231,0.18)" }}
          >
            {milestone.emoji}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80 flex items-center gap-1">
              <Sparkle size={11} weight="fill" /> New milestone
            </p>
            <h3 className="font-heading font-black text-lg leading-tight mt-0.5" data-testid="handicap-milestone-label">
              {milestone.label} {milestone.emoji}
            </h3>
            <p className="text-xs opacity-85 mt-0.5">{milestone.tagline}</p>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="handicap-milestone-share-btn"
              className="mt-3 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-heading font-bold"
              style={{ background: "#F2EEE7", color: "#143D2F" }}
            >
              <Share size={14} weight="fill" /> Share to mates
            </button>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
            style={{ background: "rgba(20,61,47,0.65)" }}
            onClick={() => setOpen(false)}
            data-testid="handicap-milestone-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 22, stiffness: 260 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm relative"
            >
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="handicap-milestone-modal-close"
                className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center shadow-lg"
                style={{ background: "#F2EEE7", color: "#143D2F" }}
              >
                <X size={18} weight="bold" />
              </button>

              <div
                ref={cardRef}
                data-testid="handicap-milestone-card"
                className="rounded-3xl overflow-hidden"
                style={{
                  background: `linear-gradient(160deg, #143D2F 0%, ${milestone.accent}55 70%, #0c2a20 100%)`,
                  color: "#F2EEE7",
                  width: "100%",
                  aspectRatio: "9 / 16",
                  position: "relative",
                  fontFamily: "Inter, system-ui, sans-serif",
                }}
              >
                {/* Decorative rings */}
                <div
                  style={{
                    position: "absolute",
                    top: -120,
                    right: -120,
                    width: 360,
                    height: 360,
                    borderRadius: "50%",
                    background: `${milestone.accent}33`,
                    filter: "blur(2px)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: -90,
                    left: -90,
                    width: 240,
                    height: 240,
                    borderRadius: "50%",
                    background: "rgba(196,84,60,0.18)",
                    filter: "blur(2px)",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    padding: "28px 24px 24px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 22 }}>
                    <Target size={20} weight="fill" color={milestone.accent} />
                    <span
                      style={{
                        fontSize: 13,
                        letterSpacing: 4,
                        textTransform: "uppercase",
                        opacity: 0.75,
                        fontWeight: 700,
                      }}
                    >
                      Fairway
                    </span>
                  </div>

                  <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "center" }}>
                    <div style={{ fontSize: 72, lineHeight: 1, marginBottom: 8 }}>{milestone.emoji}</div>
                    <div
                      style={{
                        fontSize: 11,
                        letterSpacing: 3,
                        textTransform: "uppercase",
                        opacity: 0.7,
                        fontWeight: 700,
                      }}
                    >
                      New milestone
                    </div>
                    <div style={{ fontSize: 30, fontWeight: 900, lineHeight: 1.1, marginTop: 8 }}>
                      {milestone.label}
                    </div>
                    {userName && (
                      <div style={{ fontSize: 14, opacity: 0.75, marginTop: 6, fontWeight: 600 }}>
                        — {userName}
                      </div>
                    )}

                    <div
                      style={{
                        marginTop: 28,
                        padding: "16px 20px",
                        borderRadius: 20,
                        background: "rgba(242,238,231,0.1)",
                        border: "1px solid rgba(242,238,231,0.18)",
                        display: "inline-block",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          letterSpacing: 3,
                          textTransform: "uppercase",
                          opacity: 0.65,
                          fontWeight: 700,
                        }}
                      >
                        Handicap index
                      </div>
                      <div
                        style={{
                          fontSize: 56,
                          fontWeight: 900,
                          fontVariantNumeric: "tabular-nums",
                          lineHeight: 1,
                          marginTop: 4,
                        }}
                      >
                        {milestone.handicap_index.toFixed(1)}
                      </div>
                    </div>

                    <div style={{ marginTop: 22, fontSize: 13, opacity: 0.7, fontStyle: "italic" }}>
                      "{milestone.tagline}"
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: "auto",
                      fontSize: 11,
                      opacity: 0.5,
                      letterSpacing: 1.5,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      textAlign: "center",
                    }}
                  >
                    fairway · uk golf
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={share}
                  disabled={busy}
                  data-testid="handicap-milestone-modal-share"
                  className="btn-primary flex-1"
                >
                  {busy ? "Generating…" : (
                    <>
                      {navigator.canShare ? <Share size={18} weight="fill" /> : <Download size={18} weight="fill" />}
                      {navigator.canShare ? "Share milestone" : "Save image"}
                    </>
                  )}
                </button>
              </div>
              {toast && (
                <div
                  data-testid="handicap-milestone-toast"
                  className="text-center text-xs font-bold mt-2 font-heading"
                  style={{ color: "#F2EEE7" }}
                >
                  {toast}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
